@file:Suppress("ktlint:standard:no-wildcard-imports")

package xyz.malefic.multipage.pages

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import xyz.malefic.multipage.components.Footer
import xyz.malefic.multipage.components.Header
import xyz.malefic.multipage.components.StickyContactBar
import xyz.malefic.multipage.i18n.t
import xyz.malefic.multipage.styles.Theme

@Page("/services")
@Composable
fun ServicesPage() {
    Column(
        modifier =
            Modifier
                .fillMaxSize()
                .backgroundColor(Theme.Background),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Header()
        ServicesHero()
        ServiceDetailSection()
        MidCtaBandServices()
        PricingSection()
        FlowSection()
        ServiceCtaSection()
        Footer()
        StickyContactBar()
    }
}

@Composable
private fun ServicesHero() {
    Box(
        modifier =
            Modifier
                .fillMaxWidth()
                .padding(topBottom = 80.px)
                .backgroundColor(Theme.Primary),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier =
                Modifier
                    .maxWidth(700.px)
                    .padding(leftRight = 24.px),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            SpanText(
                text = t("services.page.title"),
                modifier =
                    Modifier
                        .fontFamily(Theme.HeadingFontFamily)
                        .fontSize(40.px)
                        .fontWeight(FontWeight.Bold)
                        .color(Theme.Surface)
                        .textAlign(TextAlign.Center)
                        .margin(bottom = 20.px)
                        .letterSpacing(2.px),
            )
            SpanText(
                text = t("services.page.subtitle"),
                modifier =
                    Modifier
                        .fontSize(17.px)
                        .color(rgba(255, 255, 255, 0.85f))
                        .textAlign(TextAlign.Center)
                        .lineHeight(1.8),
            )
        }
    }
}

@Composable
private fun ServiceDetailSection() {
    Column(
        modifier =
            Modifier
                .fillMaxWidth()
                .padding(topBottom = 96.px)
                .backgroundColor(Theme.Surface),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Column(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .maxWidth(900.px)
                    .padding(leftRight = 24.px)
                    .gap(64.px),
        ) {
            ServiceDetail(
                icon = "\uD83D\uDCE6",
                title = t("services.ihinseiri.title"),
                description = t("services.ihinseiri.detail"),
                features =
                    listOf(
                        t("services.ihinseiri.f1"),
                        t("services.ihinseiri.f2"),
                        t("services.ihinseiri.f3"),
                        t("services.ihinseiri.f4"),
                    ),
            )

            Divider()

            ServiceDetail(
                icon = "\uD83C\uDFE0",
                title = t("services.akiya.title"),
                description = t("services.akiya.detail"),
                features =
                    listOf(
                        t("services.akiya.f1"),
                        t("services.akiya.f2"),
                        t("services.akiya.f3"),
                        t("services.akiya.f4"),
                    ),
            )

            Divider()

            ServiceDetail(
                icon = "\uD83D\uDCCB",
                title = t("services.zaimu.title"),
                description = t("services.zaimu.detail"),
                features =
                    listOf(
                        t("services.zaimu.f1"),
                        t("services.zaimu.f2"),
                        t("services.zaimu.f3"),
                        t("services.zaimu.f4"),
                    ),
            )

            Divider()

            ServiceDetail(
                icon = "\u2728",
                title = t("services.tokushu.title"),
                description = t("services.tokushu.detail"),
                features =
                    listOf(
                        t("services.tokushu.f1"),
                        t("services.tokushu.f2"),
                        t("services.tokushu.f3"),
                        t("services.tokushu.f4"),
                    ),
            )
        }
    }
}

@Composable
private fun Divider() {
    Box(
        modifier =
            Modifier
                .fillMaxWidth()
                .height(1.px)
                .backgroundColor(Theme.BorderLight),
    )
}

@Composable
private fun ServiceDetail(
    icon: String,
    title: String,
    description: String,
    features: List<String>,
) {
    Column(
        modifier =
            Modifier
                .fillMaxWidth(),
    ) {
        Row(
            modifier = Modifier.gap(16.px).margin(bottom = 20.px),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier =
                    Modifier
                        .size(56.px)
                        .backgroundColor(rgba(72, 90, 120, 0.07f))
                        .borderRadius(50.percent),
                contentAlignment = Alignment.Center,
            ) {
                SpanText(icon, modifier = Modifier.fontSize(28.px))
            }

            SpanText(
                text = title,
                modifier =
                    Modifier
                        .fontFamily(Theme.HeadingFontFamily)
                        .fontSize(26.px)
                        .fontWeight(FontWeight.Bold)
                        .color(Theme.TextPrimary)
                        .letterSpacing(1.px),
            )
        }

        SpanText(
            text = description,
            modifier =
                Modifier
                    .fontSize(16.px)
                    .color(Theme.TextSecondary)
                    .lineHeight(2.0)
                    .margin(bottom = 28.px),
        )

        // Feature list in a card
        Column(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .backgroundColor(Theme.SurfaceVariant)
                    .borderRadius(Theme.RadiusMd.px)
                    .padding(24.px)
                    .gap(14.px),
        ) {
            features.forEach { feature ->
                Row(
                    modifier = Modifier.gap(12.px),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Box(
                        modifier =
                            Modifier
                                .size(22.px)
                                .backgroundColor(Theme.Accent)
                                .borderRadius(50.percent),
                        contentAlignment = Alignment.Center,
                    ) {
                        SpanText(
                            "\u2713",
                            modifier =
                                Modifier
                                    .color(Theme.Surface)
                                    .fontWeight(FontWeight.Bold)
                                    .fontSize(12.px),
                        )
                    }
                    SpanText(
                        text = feature,
                        modifier =
                            Modifier
                                .fontSize(15.px)
                                .color(Theme.TextPrimary)
                                .lineHeight(1.6),
                    )
                }
            }
        }
    }
}

@Composable
private fun MidCtaBandServices() {
    Box(
        modifier =
            Modifier
                .fillMaxWidth()
                .padding(topBottom = 40.px)
                .backgroundColor(Theme.Primary),
        contentAlignment = Alignment.Center,
    ) {
        Row(
            modifier =
                Modifier
                    .maxWidth(800.px)
                    .padding(leftRight = 24.px)
                    .gap(24.px)
                    .flexWrap(FlexWrap.Wrap),
            horizontalArrangement = Arrangement.Center,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SpanText(
                text = t("cta.title"),
                modifier =
                    Modifier
                        .color(Theme.Surface)
                        .fontSize(18.px)
                        .fontWeight(FontWeight.Bold)
                        .letterSpacing(1.px),
            )
            Box(
                modifier =
                    Modifier
                        .padding(leftRight = 32.px, topBottom = 14.px)
                        .backgroundColor(Theme.Secondary)
                        .borderRadius(Theme.RadiusMd.px)
                        .cursor(Cursor.Pointer)
                        .onClick {
                            js("window.location.href = 'mailto:info@livstayjapan.co.jp'")
                        },
                contentAlignment = Alignment.Center,
            ) {
                SpanText(
                    text = t("cta.email"),
                    modifier =
                        Modifier
                            .fontWeight(FontWeight.Bold)
                            .color(Theme.Surface)
                            .fontSize(15.px),
                )
            }
        }
    }
}

@Composable
private fun PricingSection() {
    Box(
        modifier =
            Modifier
                .fillMaxWidth()
                .padding(topBottom = 96.px)
                .backgroundColor(Theme.SurfaceVariant),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .maxWidth(1100.px)
                    .padding(leftRight = 24.px),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            SectionHeading(
                title = t("pricing.title"),
                subtitle = t("pricing.subtitle"),
            )

            Row(
                modifier =
                    Modifier
                        .fillMaxWidth()
                        .margin(top = 56.px)
                        .gap(28.px)
                        .flexWrap(FlexWrap.Wrap),
                horizontalArrangement = Arrangement.Center,
            ) {
                PricingCard(
                    name = t("pricing.basic.name"),
                    price = t("pricing.basic.price"),
                    description = t("pricing.basic.desc"),
                    features =
                        listOf(
                            t("pricing.basic.f1"),
                            t("pricing.basic.f2"),
                            t("pricing.basic.f3"),
                        ),
                    isHighlighted = false,
                )

                PricingCard(
                    name = t("pricing.standard.name"),
                    price = t("pricing.standard.price"),
                    description = t("pricing.standard.desc"),
                    features =
                        listOf(
                            t("pricing.standard.f1"),
                            t("pricing.standard.f2"),
                            t("pricing.standard.f3"),
                            t("pricing.standard.f4"),
                        ),
                    isHighlighted = true,
                )

                PricingCard(
                    name = t("pricing.premium.name"),
                    price = t("pricing.premium.price"),
                    description = t("pricing.premium.desc"),
                    features =
                        listOf(
                            t("pricing.premium.f1"),
                            t("pricing.premium.f2"),
                            t("pricing.premium.f3"),
                            t("pricing.premium.f4"),
                            t("pricing.premium.f5"),
                        ),
                    isHighlighted = false,
                )
            }

            SpanText(
                text = t("pricing.note"),
                modifier =
                    Modifier
                        .margin(top = 40.px)
                        .fontSize(14.px)
                        .color(Theme.TextTertiary)
                        .textAlign(TextAlign.Center)
                        .lineHeight(1.8),
            )
        }
    }
}

@Composable
private fun PricingCard(
    name: String,
    price: String,
    description: String,
    features: List<String>,
    isHighlighted: Boolean,
) {
    Column(
        modifier =
            Modifier
                .width(320.px)
                .padding(36.px)
                .backgroundColor(Theme.Surface)
                .borderRadius(Theme.RadiusLg.px)
                .boxShadow(
                    offsetY = if (isHighlighted) 6.px else 2.px,
                    blurRadius = if (isHighlighted) 24.px else 12.px,
                    spreadRadius = 0.px,
                    color = Theme.ShadowColor,
                ).border(
                    width = if (isHighlighted) 2.px else 1.px,
                    style = LineStyle.Solid,
                    color = if (isHighlighted) Theme.Primary else Theme.BorderLight,
                ),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        // Recommended badge
        if (isHighlighted) {
            Box(
                modifier =
                    Modifier
                        .padding(leftRight = 16.px, topBottom = 4.px)
                        .backgroundColor(Theme.Primary)
                        .borderRadius(Theme.RadiusSm.px)
                        .margin(bottom = 16.px),
                contentAlignment = Alignment.Center,
            ) {
                SpanText(
                    text = t("pricing.recommended"),
                    modifier =
                        Modifier
                            .color(Theme.Surface)
                            .fontSize(12.px)
                            .fontWeight(FontWeight.Bold),
                )
            }
        }

        SpanText(
            text = name,
            modifier =
                Modifier
                    .fontSize(15.px)
                    .fontWeight(FontWeight.SemiBold)
                    .color(Theme.Primary)
                    .margin(bottom = 12.px)
                    .letterSpacing(1.px),
        )

        SpanText(
            text = price,
            modifier =
                Modifier
                    .fontSize(32.px)
                    .fontWeight(FontWeight.Bold)
                    .color(Theme.TextPrimary)
                    .margin(bottom = 12.px),
        )

        SpanText(
            text = description,
            modifier =
                Modifier
                    .fontSize(14.px)
                    .color(Theme.TextSecondary)
                    .textAlign(TextAlign.Center)
                    .margin(bottom = 28.px)
                    .lineHeight(1.6),
        )

        Box(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .height(1.px)
                    .backgroundColor(Theme.BorderLight)
                    .margin(bottom = 28.px),
        )

        Column(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .gap(14.px)
                    .margin(bottom = 28.px),
        ) {
            features.forEach { feature ->
                Row(
                    modifier = Modifier.gap(10.px),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    SpanText(
                        "\u2713",
                        modifier =
                            Modifier
                                .color(Theme.Accent)
                                .fontWeight(FontWeight.Bold)
                                .fontSize(15.px),
                    )
                    SpanText(
                        text = feature,
                        modifier =
                            Modifier
                                .fontSize(15.px)
                                .color(Theme.TextPrimary)
                                .lineHeight(1.5),
                    )
                }
            }
        }

        Box(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .padding(topBottom = 14.px)
                    .backgroundColor(if (isHighlighted) Theme.Primary else Theme.SurfaceVariant)
                    .borderRadius(Theme.RadiusMd.px)
                    .cursor(Cursor.Pointer)
                    .onClick {
                        js("window.location.href = 'mailto:info@livstayjapan.co.jp'")
                    },
            contentAlignment = Alignment.Center,
        ) {
            SpanText(
                text = t("hero.cta"),
                modifier =
                    Modifier
                        .fontWeight(FontWeight.Bold)
                        .fontSize(15.px)
                        .color(if (isHighlighted) Theme.Surface else Theme.TextPrimary),
            )
        }
    }
}

@Composable
private fun FlowSection() {
    Column(
        modifier =
            Modifier
                .fillMaxWidth()
                .padding(topBottom = 96.px)
                .backgroundColor(Theme.Surface),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        SectionHeading(
            title = t("flow.title"),
            subtitle = t("flow.subtitle"),
        )

        Row(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .maxWidth(1100.px)
                    .margin(top = 56.px)
                    .padding(leftRight = 24.px)
                    .gap(12.px)
                    .flexWrap(FlexWrap.Wrap),
            horizontalArrangement = Arrangement.Center,
        ) {
            FlowStep("1", t("flow.step1.title"), t("flow.step1.desc"), showArrow = true)
            FlowStep("2", t("flow.step2.title"), t("flow.step2.desc"), showArrow = true)
            FlowStep("3", t("flow.step3.title"), t("flow.step3.desc"), showArrow = true)
            FlowStep("4", t("flow.step4.title"), t("flow.step4.desc"), showArrow = true)
            FlowStep("5", t("flow.step5.title"), t("flow.step5.desc"), showArrow = false)
        }
    }
}

@Composable
private fun FlowStep(
    number: String,
    title: String,
    description: String,
    showArrow: Boolean,
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Column(
            modifier =
                Modifier
                    .width(170.px)
                    .padding(16.px),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Box(
                modifier =
                    Modifier
                        .size(48.px)
                        .backgroundColor(Theme.Primary)
                        .borderRadius(50.percent)
                        .margin(bottom = 14.px),
                contentAlignment = Alignment.Center,
            ) {
                SpanText(
                    text = number,
                    modifier =
                        Modifier
                            .color(Theme.Surface)
                            .fontWeight(FontWeight.Bold)
                            .fontSize(20.px),
                )
            }

            SpanText(
                text = title,
                modifier =
                    Modifier
                        .fontSize(16.px)
                        .fontWeight(FontWeight.Bold)
                        .color(Theme.TextPrimary)
                        .textAlign(TextAlign.Center)
                        .margin(bottom = 8.px)
                        .letterSpacing(0.5.px),
            )

            SpanText(
                text = description,
                modifier =
                    Modifier
                        .fontSize(13.px)
                        .color(Theme.TextSecondary)
                        .textAlign(TextAlign.Center)
                        .lineHeight(1.6),
            )
        }

        if (showArrow) {
            SpanText(
                "\u203A",
                modifier =
                    Modifier
                        .fontSize(28.px)
                        .color(Theme.TextTertiary)
                        .margin(leftRight = 4.px),
            )
        }
    }
}

@Composable
private fun ServiceCtaSection() {
    Box(
        modifier =
            Modifier
                .fillMaxWidth()
                .padding(topBottom = 96.px)
                .backgroundColor(Theme.Primary),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .maxWidth(700.px)
                    .padding(leftRight = 24.px),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            SpanText(
                text = t("cta.title"),
                modifier =
                    Modifier
                        .fontFamily(Theme.HeadingFontFamily)
                        .color(Theme.Surface)
                        .fontSize(32.px)
                        .fontWeight(FontWeight.Bold)
                        .textAlign(TextAlign.Center)
                        .margin(bottom = 16.px)
                        .letterSpacing(1.px),
            )

            SpanText(
                text = t("cta.subtitle"),
                modifier =
                    Modifier
                        .color(rgba(255, 255, 255, 0.8f))
                        .fontSize(16.px)
                        .textAlign(TextAlign.Center)
                        .margin(bottom = 12.px)
                        .lineHeight(1.8),
            )

            SpanText(
                text = "info@livstayjapan.co.jp",
                modifier =
                    Modifier
                        .color(rgba(255, 255, 255, 0.6f))
                        .fontSize(14.px)
                        .margin(bottom = 40.px),
            )

            Box(
                modifier =
                    Modifier
                        .padding(leftRight = 48.px, topBottom = 18.px)
                        .backgroundColor(Theme.Secondary)
                        .borderRadius(Theme.RadiusMd.px)
                        .cursor(Cursor.Pointer)
                        .boxShadow(offsetY = 4.px, blurRadius = 16.px, color = rgba(180, 150, 100, 0.4f))
                        .onClick {
                            js("window.location.href = 'mailto:info@livstayjapan.co.jp'")
                        },
                contentAlignment = Alignment.Center,
            ) {
                SpanText(
                    text = t("cta.email"),
                    modifier =
                        Modifier
                            .fontWeight(FontWeight.Bold)
                            .color(Theme.Surface)
                            .fontSize(17.px)
                            .letterSpacing(1.px),
                )
            }
        }
    }
}
